import React from 'react';
import { useDarkMode } from 'utils/darkMode';
import {
  BesperBotProductMarkGroup,
  BesperDatasetProductMarkGroup,
  BesperPlatformSlatMarkGroup,
} from 'besper-product-marks';

const DATASET_BACKEND_IMAGE_CONFIG = {
  file: {
    type: 'dataset_backend_logo',
    logoUrl: '/icons/tech/minio.png',
    bgColor: '#fff7f7',
    imageStyle: { width: '74%', height: '38%' },
  },
  postgres: {
    type: 'dataset_backend_logo',
    logoUrl: '/icons/tech/postgres.png',
    bgColor: '#f8fbff',
    imageStyle: { width: '58%', height: '58%' },
  },
  clickhouse: {
    type: 'dataset_backend_logo',
    logoUrl: '/icons/tech/clickhouse.png',
    bgColor: '#fffdf4',
    imageStyle: { width: '76%', height: '50%' },
  },
  victoriametrics: {
    type: 'dataset_backend_logo',
    logoUrl: '/icons/tech/victoriametrics.png',
    bgColor: '#4d0e82',
    imageStyle: { width: '80%', height: '42%' },
  },
};

function normalizeDatasetDestination(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw || raw === 'file' || raw === 'blob' || raw === 'storage') return 'file';
  if (raw === 'postgres' || raw === 'postgresql' || raw === 'warehouse') return 'postgres';
  if (raw === 'clickhouse' || raw === 'ch' || raw === 'olap') return 'clickhouse';
  if (raw === 'tigerbeetle' || raw === 'tb' || raw === 'ledger') return 'tigerbeetle';
  if (raw === 'victoriametrics' || raw === 'vm' || raw === 'timeseries' || raw === 'tsdb') {
    return 'victoriametrics';
  }
  return raw;
}

function getDatasetBackendConfig(destination) {
  const normalized = normalizeDatasetDestination(destination);
  if (normalized === 'tigerbeetle') {
    return {
      type: 'dataset_backend_logo',
      logoUrl: '/icons/tech/tigerbeetle.png',
      bgColor: '#fff7ed',
      imageStyle: { width: '68%', height: '68%' },
      iconColor: '#3f2a1d',
      accentColor: '#c2410c',
    };
  }
  if (DATASET_BACKEND_IMAGE_CONFIG[normalized]) {
    return {
      ...DATASET_BACKEND_IMAGE_CONFIG[normalized],
      iconColor: '#022d54',
      accentColor: '#022d54',
    };
  }
  return null;
}

/**
 * Product SVG Icon Component
 * Palantir-style geometric SVG icons for each B-esper product
 */
export const ProductSVGIcon = ({
  productName,
  productId,
  productType,
  /** Stripe/catalog `metadata.product_key` (e.g. platform_onprem). */
  productKey,
  /** When `productType` is dataset: kind-specific geometric icon (from `getDatasetCardVisual`). */
  datasetIconVariant,
  /** Dataset backend / destination (e.g. blob, postgres, clickhouse, victoriametrics). */
  datasetDestination,
  size = 64,
  className = '',
}) => {
  const { isDarkMode } = useDarkMode();
  const name = (productName || '').toLowerCase();
  const id = (productId || '').toLowerCase();
  const catalogKey = (productKey || '').toString().trim().toLowerCase();
  let type = (productType || '').toLowerCase();
  // Derive type from resource ID when not provided or backend-only (e.g. "operational")
  // Resource IDs: ai_lab-op-xxx, resale-op-xxx, bot-op-xxx, etc.
  if (!type || ['operational', 'free', 'demo'].includes(type)) {
    if (id.startsWith('ai_lab-')) type = 'ai_lab';
    else if (id.startsWith('resale-')) type = 'resale';
    else if (id.startsWith('bot-')) type = 'bot';
    else if (id.startsWith('odoo-')) type = 'hosted_app';
    else if (id.startsWith('labs-')) type = 'labs';
    else if (id.startsWith('dataset-')) type = 'dataset';
    else if (id.startsWith('keyvault-') || id.startsWith('key_vault-')) type = 'key_vault';
  }
  // eslint-disable-next-line no-unused-vars
  const combined = name + id;
  
  // Determine icon type and color based on product type, name, or ID
  // Use B-esper brand colors for native products
  // Keep the same beautiful style in both modes: solid light backgrounds with dark icon lines
  const getIconConfig = () => {
    if (
      catalogKey === 'platform_onprem' ||
      id === 'platform_onprem' ||
      id.includes('platform_onprem')
    ) {
      return {
        type: 'platform_onprem',
        bgColor: '#f0f9ff',
        iconColor: '#022d54',
        accentColor: '#4a90e2',
      };
    }

    // Type-based matching takes priority (most reliable)
    // AI Labs - matches type 'ai_lab' or 'labs'
    if (type === 'ai_lab' || type === 'labs') {
      return { 
        type: 'finance', // Uses the labs/finance icon
        bgColor: '#f0fdf4', // Light green background
        iconColor: '#022d54', // Dark blue icon lines
        accentColor: '#10b981' // Professional green
      };
    }
    
    // Resale - matches type 'resale'
    if (type === 'resale') {
      return { 
        type: 'resale', 
        bgColor: '#faf5ff', // Light purple background
        iconColor: '#022d54', // Dark blue icon lines
        accentColor: '#8b5cf6' // Professional purple
      };
    }
    
    // Bot - matches type 'bot'
    if (type === 'bot') {
      return { 
        type: 'bot', 
        bgColor: '#f0f9ff', // Light blue background
        iconColor: '#022d54', // Dark blue icon lines
        accentColor: '#4a90e2' // B-esper primary light blue
      };
    }

    // Dataset — per-kind icons when variant is passed (dashboard cards); else stacked cylinders
    if (
      type === 'dataset' ||
      (name && String(name).toLowerCase().includes('dataset')) ||
      id.includes('dataset_cylinder')
    ) {
      const backendConfig = getDatasetBackendConfig(datasetDestination);
      if (backendConfig) {
        return backendConfig;
      }
      const v = datasetIconVariant;
      if (v === 'dataset_manual') {
        return { type: 'dataset_manual', bgColor: '#eef2ff', iconColor: '#022d54', accentColor: '#6366f1' };
      }
      if (v === 'dataset_push') {
        return { type: 'dataset_push', bgColor: '#ecfeff', iconColor: '#022d54', accentColor: '#0d9488' };
      }
      if (v === 'dataset_extractor') {
        return { type: 'dataset_extractor', bgColor: '#fffbeb', iconColor: '#022d54', accentColor: '#d97706' };
      }
      if (v === 'dataset_resource_sessions') {
        return { type: 'dataset_resource_sessions', bgColor: '#ecfeff', iconColor: '#022d54', accentColor: '#0891b2' };
      }
      if (v === 'dataset_resource_knowledge') {
        return { type: 'dataset_resource_knowledge', bgColor: '#f0f9ff', iconColor: '#022d54', accentColor: '#0284c7' };
      }
      if (v === 'dataset_resource_storage') {
        return { type: 'dataset_resource_storage', bgColor: '#f0fdfa', iconColor: '#022d54', accentColor: '#0d9488' };
      }
      if (v === 'dataset_resource_analytics') {
        return { type: 'dataset', bgColor: '#ecfeff', iconColor: '#022d54', accentColor: '#0891b2' };
      }
      if (v === 'dataset_resource_generic') {
        return { type: 'dataset_resource_generic', bgColor: '#ecfeff', iconColor: '#022d54', accentColor: '#0891b2' };
      }
      if (v === 'dataset_unset') {
        return { type: 'dataset_unset', bgColor: '#f8fafc', iconColor: '#022d54', accentColor: '#64748b' };
      }
      return {
        type: 'dataset',
        bgColor: '#ecfeff',
        iconColor: '#022d54',
        accentColor: '#0891b2'
      };
    }

    // Customer Key Vault resource (secrets stored in Azure KV; card title often "Secrets — …")
    if (
      type === 'key_vault' ||
      name.includes('key vault') ||
      name.includes('key-vault') ||
      id.includes('keyvault') ||
      name.startsWith('secrets')
    ) {
      return {
        type: 'key_vault',
        bgColor: '#f1f5f9',
        iconColor: '#022d54',
        accentColor: '#0ea5e9'
      };
    }

    if (type === 'hosted_app') {
      return {
        type: 'hosted_app',
        bgColor: '#f0fdfa',
        iconColor: '#022d54',
        accentColor: '#14b8a6'
      };
    }
    
    // Name-based matching (fallback for when type is not provided)
    // Besper Bot - Neural network / AI brain (B-esper brand blue)
    if (name.includes('besper bot') || name.includes('b-esper bot') || id.includes('besper-bot')) {
      return { 
        type: 'bot', 
        bgColor: '#f0f9ff', // Same beautiful light blue background in both modes
        iconColor: '#022d54', // Same dark blue icon lines in both modes
        accentColor: '#4a90e2' // B-esper primary light blue
      };
    }
    
    // B-esper Labs - Isometric cube representing AI lab infrastructure
    if (name.includes('besper labs') || name.includes('b-esper labs') || name.includes('besper-labs') || (name.includes('labs') && name.includes('besper'))) {
      return { 
        type: 'finance', 
        bgColor: '#f0fdf4', // Same beautiful light green background in both modes
        iconColor: '#022d54', // Same dark blue icon lines in both modes
        accentColor: '#10b981' // Professional green
      };
    }
    
    // Resale UI - Marketplace/storefront geometric - Professional purple
    if (name.includes('resale') || name.includes('resale ui')) {
      return { 
        type: 'resale', 
        bgColor: '#faf5ff', // Same beautiful light purple background in both modes
        iconColor: '#022d54', // Same dark blue icon lines in both modes
        accentColor: '#8b5cf6' // Professional purple
      };
    }
    
    // SaaS B2B Hosting icons - Base blue for standard features
    if (id.includes('b2b-hosting') || (name.includes('saas') && name.includes('b2b') && name.includes('hosting'))) {
      return { 
        type: 'b2b-hosting', 
        bgColor: '#f0f9ff', 
        iconColor: '#022d54', 
        accentColor: '#4a90e2' 
      };
    }
    if (id.includes('security-integration') || (name.includes('security') && name.includes('integration'))) {
      return { 
        type: 'security-integration', 
        bgColor: '#f0f9ff', 
        iconColor: '#022d54', 
        accentColor: '#4a90e2' 
      };
    }
    if (id.includes('billing-monitoring') || (name.includes('billing') && name.includes('monitoring'))) {
      return { 
        type: 'billing-monitoring', 
        bgColor: '#f0f9ff', 
        iconColor: '#022d54', 
        accentColor: '#4a90e2' 
      };
    }
    
    // SaaS B2C Hosting icons - Base blue for standard features
    if (id.includes('seamless-integration') || (name.includes('seamless') && name.includes('integration'))) {
      return { 
        type: 'seamless-integration', 
        bgColor: '#f0f9ff', 
        iconColor: '#022d54', 
        accentColor: '#4a90e2' 
      };
    }
    if (id.includes('domain-connection') || (name.includes('domain') && name.includes('connection'))) {
      return { 
        type: 'domain-connection', 
        bgColor: '#f0f9ff', 
        iconColor: '#022d54', 
        accentColor: '#4a90e2' 
      };
    }
    if (id.includes('managed-infrastructure') || (name.includes('managed') && name.includes('infrastructure'))) {
      return { 
        type: 'managed-infrastructure', 
        bgColor: '#f0f9ff', 
        iconColor: '#022d54', 
        accentColor: '#4a90e2' 
      };
    }
    if (id.includes('data-sovereignty') || (name.includes('data') && name.includes('sovereignty'))) {
      return { 
        type: 'data-sovereignty', 
        bgColor: '#f0f9ff', 
        iconColor: '#022d54', 
        accentColor: '#4a90e2' 
      };
    }
    
    // Resale tab icons - Base blue for standard features
    if (id.includes('resale-management') || (name.includes('resale') && name.includes('management'))) {
      return { 
        type: 'resale-management', 
        bgColor: '#f0f9ff', 
        iconColor: '#022d54', 
        accentColor: '#4a90e2' 
      };
    }
    if (id.includes('embeddable-sdk') || (name.includes('embeddable') && name.includes('sdk'))) {
      return { 
        type: 'embeddable-sdk', 
        bgColor: '#f0f9ff', 
        iconColor: '#022d54', 
        accentColor: '#4a90e2' 
      };
    }
    if (id.includes('demo-sdk') || (name.includes('demo') && name.includes('sdk'))) {
      return { 
        type: 'demo-sdk', 
        bgColor: '#f0f9ff', 
        iconColor: '#022d54', 
        accentColor: '#4a90e2' 
      };
    }
    if (id.includes('partner-network') || (name.includes('partner') && name.includes('network'))) {
      return { 
        type: 'partner-network', 
        bgColor: '#f0f9ff', 
        iconColor: '#022d54', 
        accentColor: '#4a90e2' 
      };
    }
    
    // Marketplace tab icons - Base blue for standard features
    if (id.includes('marketplace-platform') || (name.includes('marketplace') && name.includes('platform'))) {
      return { 
        type: 'marketplace-platform', 
        bgColor: '#f0f9ff', 
        iconColor: '#022d54', 
        accentColor: '#4a90e2' 
      };
    }
    if (id.includes('kubernetes-hosting') || (name.includes('kubernetes') && name.includes('hosting'))) {
      return { 
        type: 'kubernetes-hosting', 
        bgColor: '#f0f9ff', 
        iconColor: '#022d54', 
        accentColor: '#4a90e2' 
      };
    }
    if (id.includes('everything-included') || (name.includes('everything') && name.includes('included'))) {
      return { 
        type: 'everything-included', 
        bgColor: '#f0f9ff', 
        iconColor: '#022d54', 
        accentColor: '#4a90e2' 
      };
    }
    if (id.includes('european-sovereignty') || (name.includes('european') && name.includes('sovereignty'))) {
      return { 
        type: 'european-sovereignty', 
        bgColor: '#f0f9ff', 
        iconColor: '#022d54', 
        accentColor: '#4a90e2' 
      };
    }
    
    // Compliance tab icons
    if (id.includes('hosting-location') || (name.includes('hosting') && name.includes('location'))) {
      return { 
        type: 'hosting-location', 
        bgColor: '#f0f9ff', 
        iconColor: '#022d54', 
        accentColor: '#4a90e2' 
      };
    }
    if (id.includes('compliance-certification') || (name.includes('compliance') && name.includes('certification'))) {
      return { 
        type: 'compliance-certification', 
        bgColor: '#fdf2f8', 
        iconColor: '#022d54', 
        accentColor: '#ec4899' 
      };
    }
    if (id.includes('access-permissions') || (name.includes('access') && name.includes('permissions'))) {
      return { 
        type: 'access-permissions', 
        bgColor: '#f0f9ff', 
        iconColor: '#022d54', 
        accentColor: '#4a90e2' 
      };
    }
    if (id.includes('scim-sso') || (name.includes('scim') || name.includes('sso'))) {
      return { 
        type: 'scim-sso', 
        bgColor: '#f0f9ff', 
        iconColor: '#022d54', 
        accentColor: '#4a90e2' 
      };
    }
    
    // Default - Modular blocks - Neutral slate
    return { 
      type: 'default', 
      bgColor: '#f8fafc', // Same beautiful light gray background in both modes
      iconColor: '#022d54', // Same dark blue icon lines in both modes
      accentColor: '#64748b' // Professional slate
    };
  };
  
  const { type: iconType, bgColor, iconColor, accentColor } = getIconConfig();
  const viewBox = '0 0 64 64';
  
  // Icon definitions - Palantir-style geometric icons
  const renderIcon = () => {
    switch (iconType) {
      case 'dataset_manual': {
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            <rect x="16" y="14" width="32" height="40" rx="3" stroke={iconColor} strokeWidth="2.5" />
            <line x1="22" y1="24" x2="42" y2="24" stroke={accentColor} strokeWidth="2" />
            <line x1="22" y1="32" x2="40" y2="32" stroke={iconColor} strokeWidth="2" opacity="0.45" />
            <line x1="22" y1="40" x2="36" y2="40" stroke={iconColor} strokeWidth="2" opacity="0.45" />
          </g>
        );
      }
      case 'dataset_push': {
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 46 H52" stroke={iconColor} strokeWidth="2.5" />
            <path d="M16 46 V40 H48 V46" stroke={iconColor} strokeWidth="2" />
            <path d="M32 16 V36" stroke={accentColor} strokeWidth="2.5" />
            <path d="M26 30 L32 36 L38 30" stroke={accentColor} strokeWidth="2.5" />
          </g>
        );
      }
      case 'dataset_extractor': {
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            <rect x="8" y="28" width="12" height="12" rx="2" stroke={iconColor} strokeWidth="2" />
            <line x1="20" y1="34" x2="26" y2="34" stroke={accentColor} strokeWidth="2" />
            <path d="M26 31 L30 34 L26 37" stroke={accentColor} strokeWidth="2" fill="none" />
            <rect x="30" y="24" width="14" height="20" rx="2" stroke={accentColor} strokeWidth="2" />
            <line x1="44" y1="34" x2="50" y2="34" stroke={accentColor} strokeWidth="2" />
            <path d="M50 31 L54 34 L50 37" stroke={accentColor} strokeWidth="2" fill="none" />
            <rect x="54" y="28" width="8" height="12" rx="1.5" stroke={iconColor} strokeWidth="2" />
          </g>
        );
      }
      case 'dataset_resource_sessions': {
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            <rect x="18" y="16" width="28" height="22" rx="3" stroke={iconColor} strokeWidth="2" opacity="0.35" />
            <rect x="12" y="26" width="32" height="24" rx="3" stroke={iconColor} strokeWidth="2.5" />
            <circle cx="22" cy="38" r="3" stroke={accentColor} strokeWidth="1.8" />
            <line x1="28" y1="38" x2="40" y2="38" stroke={iconColor} strokeWidth="2" opacity="0.4" />
          </g>
        );
      }
      case 'dataset_resource_knowledge': {
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 14 H38 L44 20 V50 H18 Z" stroke={iconColor} strokeWidth="2.5" />
            <path d="M38 14 V20 H44" stroke={accentColor} strokeWidth="2" />
            <line x1="24" y1="30" x2="40" y2="30" stroke={iconColor} strokeWidth="1.8" opacity="0.45" />
            <line x1="24" y1="38" x2="38" y2="38" stroke={iconColor} strokeWidth="1.8" opacity="0.45" />
          </g>
        );
      }
      case 'dataset_resource_storage': {
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            <ellipse cx="32" cy="22" rx="20" ry="6" stroke={iconColor} strokeWidth="2.5" />
            <path d="M12 22 v14 c0 3.5 9 7 20 7 s20 -3.5 20 -7 V22" stroke={iconColor} strokeWidth="2.5" />
            <ellipse cx="32" cy="36" rx="20" ry="6" stroke={accentColor} strokeWidth="2" />
          </g>
        );
      }
      case 'dataset_resource_generic': {
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M24 28 Q18 28 18 34 Q18 40 24 40 H28" stroke={iconColor} strokeWidth="2.5" />
            <path d="M40 28 H36 Q42 28 42 34 Q42 40 36 40" stroke={iconColor} strokeWidth="2.5" />
            <line x1="28" y1="34" x2="36" y2="34" stroke={accentColor} strokeWidth="2.5" />
          </g>
        );
      }
      case 'dataset_unset': {
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            <rect x="14" y="14" width="36" height="36" rx="6" stroke={iconColor} strokeWidth="2" strokeDasharray="4 3" />
            <circle cx="32" cy="32" r="4" stroke={accentColor} strokeWidth="2" />
          </g>
        );
      }
      case 'dataset_tigerbeetle': {
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            <rect x="16" y="14" width="32" height="36" rx="4" stroke={iconColor} strokeWidth="2.5" />
            <line x1="24" y1="24" x2="40" y2="24" stroke={accentColor} strokeWidth="2.5" />
            <line x1="24" y1="32" x2="40" y2="32" stroke={iconColor} strokeWidth="2" opacity="0.45" />
            <line x1="24" y1="40" x2="34" y2="40" stroke={iconColor} strokeWidth="2" opacity="0.45" />
            <path d="M20 12 L28 18" stroke={accentColor} strokeWidth="2.5" />
            <path d="M28 12 L36 18" stroke={accentColor} strokeWidth="2.5" />
            <path d="M36 12 L44 18" stroke={accentColor} strokeWidth="2.5" />
          </g>
        );
      }
      case 'dataset': {
        return (
          <BesperDatasetProductMarkGroup iconColor={iconColor} accentColor={accentColor} />
        );
      }
      case 'platform_onprem': {
        return <BesperPlatformSlatMarkGroup iconColor={iconColor} />;
      }

      case 'key_vault': {
        // Secure vault aperture — arch + jambs + locking bar (no padlock; geometric / enterprise)
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path
              d="M17 52 V29 Q17 14 32 14 Q47 14 47 29 V52"
              stroke={iconColor}
              strokeWidth="2.5"
            />
            <path
              d="M23 52 V33 Q23 24 32 24 Q41 24 41 33 V52"
              stroke={iconColor}
              strokeWidth="1.75"
              opacity="0.32"
            />
            <line x1="20" y1="52" x2="44" y2="52" stroke={iconColor} strokeWidth="2.5" />
            <line x1="25" y1="37" x2="39" y2="37" stroke={accentColor} strokeWidth="2.25" />
            <line x1="32" y1="37" x2="32" y2="44" stroke={accentColor} strokeWidth="2" opacity="0.9" />
          </g>
        );
      }

      case 'bot': {
        return <BesperBotProductMarkGroup iconColor={iconColor} />;
      }

      case 'hosted_app': {
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            <rect x="14" y="14" width="16" height="16" rx="2" stroke={iconColor} strokeWidth="2" />
            <rect x="34" y="14" width="16" height="16" rx="2" stroke={iconColor} strokeWidth="2" />
            <rect x="14" y="34" width="16" height="16" rx="2" stroke={iconColor} strokeWidth="2" />
            <rect x="34" y="34" width="16" height="16" rx="2" stroke={iconColor} strokeWidth="2" />
            <circle cx="32" cy="32" r="3" fill={iconColor} />
          </g>
        );
      }
      
      case 'finance': {
        // Isometric cube - represents structured learning/modules
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            {/* 3D cube outline */}
            <path d="M32 14 L50 26 L50 42 L32 54 L14 42 L14 26 Z" stroke={iconColor} strokeWidth="3" />
            {/* Internal lines for 3D effect */}
            <line x1="32" y1="14" x2="32" y2="38" stroke={iconColor} strokeWidth="2.5" />
            <line x1="14" y1="26" x2="32" y2="38" stroke={iconColor} strokeWidth="2.5" />
            <line x1="50" y1="26" x2="32" y2="38" stroke={iconColor} strokeWidth="2.5" />
          </g>
        );
      }
      
      case 'resale': {
        // Network/marketplace - interconnected nodes
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            {/* Central square */}
            <rect x="24" y="24" width="16" height="16" stroke={iconColor} strokeWidth="3" />
            {/* Corner nodes */}
            <circle cx="16" cy="16" r="4" stroke={iconColor} strokeWidth="2.5" />
            <circle cx="48" cy="16" r="4" stroke={iconColor} strokeWidth="2.5" />
            <circle cx="16" cy="48" r="4" stroke={iconColor} strokeWidth="2.5" />
            <circle cx="48" cy="48" r="4" stroke={iconColor} strokeWidth="2.5" />
            {/* Connection lines */}
            <line x1="19" y1="19" x2="24" y2="24" stroke={iconColor} strokeWidth="2" />
            <line x1="45" y1="19" x2="40" y2="24" stroke={iconColor} strokeWidth="2" />
            <line x1="19" y1="45" x2="24" y2="40" stroke={iconColor} strokeWidth="2" />
            <line x1="45" y1="45" x2="40" y2="40" stroke={iconColor} strokeWidth="2" />
          </g>
        );
      }
      
      case 'b2b-hosting': {
        // Simple multi-cloud - Palantir style
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            {/* Three cloud shapes */}
            <path d="M12 24 Q8 20 4 22 Q2 20 2 24 Q0 24 0 28 Q0 32 4 32 L12 32 Q16 32 16 28 Q16 24 12 24" stroke={iconColor} strokeWidth="2" />
            <path d="M32 24 Q28 20 24 22 Q22 20 22 24 Q20 24 20 28 Q20 32 24 32 L32 32 Q36 32 36 28 Q36 24 32 24" stroke={iconColor} strokeWidth="2" />
            <path d="M52 24 Q48 20 44 22 Q42 20 42 24 Q40 24 40 28 Q40 32 44 32 L52 32 Q56 32 56 28 Q56 24 52 24" stroke={iconColor} strokeWidth="2" />
          </g>
        );
      }
      
      case 'security-integration': {
        // Simple security hub - Palantir style
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            {/* Central hub */}
            <circle cx="32" cy="32" r="10" stroke={iconColor} strokeWidth="2" />
            {/* Lock in center */}
            <rect x="28" y="30" width="8" height="4" rx="1" stroke={iconColor} strokeWidth="1.5" />
            <path d="M30 30 Q30 28 32 28 Q34 28 34 30" stroke={iconColor} strokeWidth="1.5" />
            {/* Connected nodes */}
            <circle cx="18" cy="20" r="3" stroke={iconColor} strokeWidth="2" />
            <circle cx="46" cy="20" r="3" stroke={iconColor} strokeWidth="2" />
            <circle cx="18" cy="44" r="3" stroke={iconColor} strokeWidth="2" />
            <circle cx="46" cy="44" r="3" stroke={iconColor} strokeWidth="2" />
            {/* Connection lines */}
            <line x1="24" y1="26" x2="20" y2="23" stroke={iconColor} strokeWidth="1.5" />
            <line x1="40" y1="26" x2="44" y2="23" stroke={iconColor} strokeWidth="1.5" />
            <line x1="24" y1="38" x2="20" y2="41" stroke={iconColor} strokeWidth="1.5" />
            <line x1="40" y1="38" x2="44" y2="41" stroke={iconColor} strokeWidth="1.5" />
          </g>
        );
      }
      
      case 'billing-monitoring': {
        // Simple dashboard chart - Palantir style
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            {/* Dashboard frame */}
            <rect x="12" y="16" width="40" height="32" rx="1.5" stroke={iconColor} strokeWidth="2" />
            {/* Chart bars */}
            <line x1="20" y1="44" x2="20" y2="38" stroke={iconColor} strokeWidth="2" />
            <line x1="28" y1="44" x2="28" y2="30" stroke={iconColor} strokeWidth="2" />
            <line x1="36" y1="44" x2="36" y2="34" stroke={iconColor} strokeWidth="2" />
            <line x1="44" y1="44" x2="44" y2="26" stroke={iconColor} strokeWidth="2" />
            {/* Trend line */}
            <path d="M20 38 L28 30 L36 34 L44 26" stroke={iconColor} strokeWidth="1.5" />
          </g>
        );
      }
      
      case 'seamless-integration': {
        // Simple integration flow - Palantir style
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            {/* Interface layer */}
            <rect x="12" y="14" width="40" height="18" rx="1.5" stroke={iconColor} strokeWidth="2" />
            <line x1="16" y1="20" x2="28" y2="20" stroke={iconColor} strokeWidth="1.5" />
            <line x1="16" y1="26" x2="36" y2="26" stroke={iconColor} strokeWidth="1.5" />
            {/* Infrastructure layer */}
            <rect x="18" y="38" width="28" height="14" rx="1.5" stroke={iconColor} strokeWidth="1.5" opacity="0.4" />
            {/* Connection */}
            <line x1="32" y1="32" x2="32" y2="38" stroke={iconColor} strokeWidth="2" />
            <circle cx="32" cy="35" r="2" fill={iconColor} />
          </g>
        );
      }
      
      case 'domain-connection': {
        // Simple domain to server - Palantir style
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            {/* Domain box */}
            <rect x="12" y="16" width="40" height="10" rx="1.5" stroke={iconColor} strokeWidth="2" />
            <line x1="16" y1="20" x2="28" y2="20" stroke={iconColor} strokeWidth="1.5" />
            {/* Connection arrow */}
            <path d="M32 26 L20 38" stroke={iconColor} strokeWidth="2" />
            <path d="M22 36 L20 38 L24 38" stroke={iconColor} strokeWidth="2" fill="none" />
            {/* Server/cloud */}
            <path d="M16 42 Q12 38 8 40 Q6 38 6 42 Q2 42 2 46 Q2 50 6 50 L20 50 Q24 50 24 46 Q24 42 20 42" stroke={iconColor} strokeWidth="2" />
          </g>
        );
      }
      
      case 'managed-infrastructure': {
        // Simple Kubernetes cluster - Palantir style
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            {/* Kubernetes nodes */}
            <rect x="12" y="18" width="14" height="14" rx="1.5" stroke={iconColor} strokeWidth="2" />
            <rect x="30" y="18" width="14" height="14" rx="1.5" stroke={iconColor} strokeWidth="2" />
            <rect x="48" y="18" width="14" height="14" rx="1.5" stroke={iconColor} strokeWidth="2" />
            {/* Pods inside nodes */}
            <rect x="15" y="21" width="4" height="4" rx="0.5" stroke={iconColor} strokeWidth="1.5" />
            <rect x="21" y="21" width="4" height="4" rx="0.5" stroke={iconColor} strokeWidth="1.5" />
            <rect x="33" y="21" width="4" height="4" rx="0.5" stroke={iconColor} strokeWidth="1.5" />
            <rect x="39" y="21" width="4" height="4" rx="0.5" stroke={iconColor} strokeWidth="1.5" />
            <rect x="51" y="21" width="4" height="4" rx="0.5" stroke={iconColor} strokeWidth="1.5" />
            {/* Connections */}
            <line x1="26" y1="25" x2="30" y2="25" stroke={iconColor} strokeWidth="1.5" opacity="0.4" />
            <line x1="44" y1="25" x2="48" y2="25" stroke={iconColor} strokeWidth="1.5" opacity="0.4" />
          </g>
        );
      }
      
      case 'data-sovereignty': {
        // Simple shield with EU circle - Palantir style
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            {/* Shield */}
            <path d="M32 12 L46 18 L46 30 Q46 40 40 46 Q32 52 24 46 Q18 40 18 30 L18 18 Z" stroke={iconColor} strokeWidth="2" />
            {/* EU circle */}
            <circle cx="32" cy="30" r="6" stroke={iconColor} strokeWidth="1.5" />
            {/* Stars */}
            <circle cx="32" cy="26" r="0.8" fill={iconColor} />
            <circle cx="35" cy="29" r="0.8" fill={iconColor} />
            <circle cx="35" cy="31" r="0.8" fill={iconColor} />
            <circle cx="32" cy="34" r="0.8" fill={iconColor} />
            <circle cx="29" cy="31" r="0.8" fill={iconColor} />
            <circle cx="29" cy="29" r="0.8" fill={iconColor} />
          </g>
        );
      }
      
      case 'resale-management': {
        // Storefront/marketplace icon
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            {/* Storefront */}
            <path d="M8 20 L32 8 L56 20 L56 48 L8 48 Z" stroke={iconColor} strokeWidth="2.5" />
            <path d="M32 8 L32 48" stroke={iconColor} strokeWidth="2.5" />
            {/* Products on shelves */}
            <rect x="14" y="28" width="8" height="8" rx="1" stroke={iconColor} strokeWidth="2" />
            <rect x="26" y="28" width="8" height="8" rx="1" stroke={iconColor} strokeWidth="2" />
            <rect x="38" y="28" width="8" height="8" rx="1" stroke={iconColor} strokeWidth="2" />
            <rect x="14" y="40" width="8" height="8" rx="1" stroke={iconColor} strokeWidth="2" />
            <rect x="26" y="40" width="8" height="8" rx="1" stroke={iconColor} strokeWidth="2" />
            <rect x="38" y="40" width="8" height="8" rx="1" stroke={iconColor} strokeWidth="2" />
            {/* Door */}
            <rect x="24" y="36" width="8" height="12" rx="1" stroke={iconColor} strokeWidth="2" />
            <circle cx="30" cy="42" r="1" fill={iconColor} />
          </g>
        );
      }
      
      case 'embeddable-sdk': {
        // Simple code block with embed - Palantir style
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            {/* Code block */}
            <rect x="16" y="20" width="32" height="18" rx="1.5" stroke={iconColor} strokeWidth="2" />
            {/* Code lines */}
            <line x1="20" y1="26" x2="28" y2="26" stroke={iconColor} strokeWidth="1.5" />
            <line x1="20" y1="32" x2="36" y2="32" stroke={iconColor} strokeWidth="1.5" />
            {/* Embed arrow */}
            <path d="M32 38 L28 44 L36 44 Z" stroke={iconColor} strokeWidth="2" fill={iconColor} />
          </g>
        );
      }
      
      case 'demo-sdk': {
        // Play button with code elements
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            {/* Play button circle */}
            <circle cx="32" cy="32" r="12" stroke={iconColor} strokeWidth="2.5" />
            {/* Play triangle */}
            <path d="M28 26 L28 38 L38 32 Z" stroke={iconColor} strokeWidth="2" fill={iconColor} />
            {/* Code brackets below */}
            <path d="M20 44 L16 48 L20 52" stroke={iconColor} strokeWidth="2" />
            <path d="M44 44 L48 48 L44 52" stroke={iconColor} strokeWidth="2" />
            <line x1="24" y1="48" x2="40" y2="48" stroke={iconColor} strokeWidth="2" />
          </g>
        );
      }
      
      case 'partner-network': {
        // Simple network nodes - Palantir style
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            {/* Central hub */}
            <circle cx="32" cy="32" r="6" stroke={iconColor} strokeWidth="2" />
            {/* Outer nodes */}
            <circle cx="18" cy="20" r="4" stroke={iconColor} strokeWidth="2" />
            <circle cx="46" cy="20" r="4" stroke={iconColor} strokeWidth="2" />
            <circle cx="18" cy="44" r="4" stroke={iconColor} strokeWidth="2" />
            <circle cx="46" cy="44" r="4" stroke={iconColor} strokeWidth="2" />
            {/* Connection lines */}
            <line x1="26" y1="28" x2="20" y2="24" stroke={iconColor} strokeWidth="1.5" />
            <line x1="38" y1="28" x2="44" y2="24" stroke={iconColor} strokeWidth="1.5" />
            <line x1="26" y1="36" x2="20" y2="40" stroke={iconColor} strokeWidth="1.5" />
            <line x1="38" y1="36" x2="44" y2="40" stroke={iconColor} strokeWidth="1.5" />
          </g>
        );
      }
      
      case 'marketplace-platform': {
        // Simple marketplace - Palantir style
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            {/* Platform base */}
            <rect x="12" y="44" width="40" height="8" rx="1" stroke={iconColor} strokeWidth="2" />
            {/* Storefront columns */}
            <rect x="16" y="24" width="7" height="18" rx="1" stroke={iconColor} strokeWidth="2" />
            <rect x="28" y="20" width="7" height="22" rx="1" stroke={iconColor} strokeWidth="2" />
            <rect x="40" y="24" width="7" height="18" rx="1" stroke={iconColor} strokeWidth="2" />
            {/* Product items */}
            <circle cx="19" cy="30" r="1.5" fill={iconColor} />
            <circle cx="31" cy="26" r="1.5" fill={iconColor} />
            <circle cx="43" cy="30" r="1.5" fill={iconColor} />
          </g>
        );
      }
      
      case 'kubernetes-hosting': {
        // Simple container infrastructure - Palantir style
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            {/* Cloud base */}
            <path d="M24 36 Q20 32 16 34 Q14 32 14 36 Q10 36 10 40 Q10 44 14 44 L28 44 Q32 44 32 40 Q32 36 28 36" stroke={iconColor} strokeWidth="2" />
            {/* Containers */}
            <rect x="16" y="48" width="5" height="5" rx="0.5" stroke={iconColor} strokeWidth="1.5" />
            <rect x="26" y="48" width="5" height="5" rx="0.5" stroke={iconColor} strokeWidth="1.5" />
            <rect x="36" y="48" width="5" height="5" rx="0.5" stroke={iconColor} strokeWidth="1.5" />
            <rect x="46" y="48" width="5" height="5" rx="0.5" stroke={iconColor} strokeWidth="1.5" />
          </g>
        );
      }
      
      case 'everything-included': {
        // Simple toolbox - Palantir style
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            {/* Toolbox */}
            <rect x="16" y="24" width="32" height="24" rx="1.5" stroke={iconColor} strokeWidth="2" />
            {/* Handle */}
            <path d="M24 24 Q28 20 32 24" stroke={iconColor} strokeWidth="2" />
            {/* Tools */}
            <line x1="22" y1="32" x2="26" y2="36" stroke={iconColor} strokeWidth="1.5" />
            <line x1="32" y1="32" x2="36" y2="36" stroke={iconColor} strokeWidth="1.5" />
            <line x1="42" y1="32" x2="46" y2="36" stroke={iconColor} strokeWidth="1.5" />
          </g>
        );
      }
      
      case 'european-sovereignty': {
        // Simple EU shield - Palantir style
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            {/* Shield */}
            <path d="M32 12 L46 18 L46 32 Q46 42 40 48 Q32 54 24 48 Q18 42 18 32 L18 18 Z" stroke={iconColor} strokeWidth="2" />
            {/* EU circle */}
            <circle cx="32" cy="32" r="7" stroke={iconColor} strokeWidth="1.5" />
            {/* EU stars */}
            <circle cx="32" cy="27" r="0.8" fill={iconColor} />
            <circle cx="34" cy="30" r="0.8" fill={iconColor} />
            <circle cx="34" cy="34" r="0.8" fill={iconColor} />
            <circle cx="32" cy="37" r="0.8" fill={iconColor} />
            <circle cx="30" cy="34" r="0.8" fill={iconColor} />
            <circle cx="30" cy="30" r="0.8" fill={iconColor} />
          </g>
        );
      }
      
      case 'hosting-location': {
        // Simple globe with location pin - Palantir style
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            {/* Globe circle */}
            <circle cx="32" cy="32" r="16" stroke={iconColor} strokeWidth="2" />
            {/* Simple grid lines */}
            <ellipse cx="32" cy="32" rx="16" ry="6" stroke={iconColor} strokeWidth="1" opacity="0.3" />
            <path d="M16 32 Q32 24 48 32" stroke={iconColor} strokeWidth="1" opacity="0.3" />
            {/* Location pin */}
            <circle cx="30" cy="40" r="2.5" fill={iconColor} />
            <path d="M30 37 L28 30 L32 30 Z" stroke={iconColor} strokeWidth="1" fill={iconColor} />
          </g>
        );
      }
      
      case 'compliance-certification': {
        // Clean shield with checkmark - Palantir style
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            {/* Shield shape */}
            <path d="M32 10 L48 16 L48 32 Q48 44 40 50 Q32 56 24 50 Q16 44 16 32 L16 16 Z" stroke={iconColor} strokeWidth="2" />
            {/* Checkmark */}
            <path d="M26 32 L30 36 L38 26" stroke={iconColor} strokeWidth="2.5" strokeLinecap="round" />
            {/* Proof indicator */}
            <circle cx="32" cy="20" r="3" stroke={iconColor} strokeWidth="1.5" />
            <path d="M31 20 L32 19 L33 20" stroke={iconColor} strokeWidth="1.5" />
          </g>
        );
      }
      
      case 'access-permissions': {
        // Simple lock - Palantir style
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            {/* Lock body */}
            <rect x="18" y="22" width="28" height="24" rx="2" stroke={iconColor} strokeWidth="2" />
            {/* Lock shackle */}
            <path d="M24 22 Q24 14 32 14 Q40 14 40 22" stroke={iconColor} strokeWidth="2" />
            {/* Keyhole */}
            <circle cx="32" cy="32" r="3" stroke={iconColor} strokeWidth="1.5" />
            <rect x="30.5" y="32" width="3" height="6" rx="0.5" stroke={iconColor} strokeWidth="1.5" />
          </g>
        );
      }
      
      case 'scim-sso': {
        // Simple identity hub - Palantir style
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            {/* Central hub */}
            <circle cx="32" cy="32" r="8" stroke={iconColor} strokeWidth="2" />
            {/* Connected nodes */}
            <circle cx="18" cy="20" r="4" stroke={iconColor} strokeWidth="2" />
            <circle cx="46" cy="20" r="4" stroke={iconColor} strokeWidth="2" />
            <circle cx="18" cy="44" r="4" stroke={iconColor} strokeWidth="2" />
            <circle cx="46" cy="44" r="4" stroke={iconColor} strokeWidth="2" />
            {/* Connection lines */}
            <line x1="24" y1="28" x2="20" y2="24" stroke={iconColor} strokeWidth="1.5" />
            <line x1="40" y1="28" x2="44" y2="24" stroke={iconColor} strokeWidth="1.5" />
            <line x1="24" y1="36" x2="20" y2="40" stroke={iconColor} strokeWidth="1.5" />
            <line x1="40" y1="36" x2="44" y2="40" stroke={iconColor} strokeWidth="1.5" />
          </g>
        );
      }
      
      case 'default':
      default: {
        // Ascending bars - clean growth chart
        return (
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            {/* Four ascending bars */}
            <line x1="16" y1="50" x2="16" y2="42" stroke={iconColor} strokeWidth="4" />
            <line x1="26" y1="50" x2="26" y2="34" stroke={iconColor} strokeWidth="4" />
            <line x1="36" y1="50" x2="36" y2="26" stroke={iconColor} strokeWidth="4" />
            <line x1="46" y1="50" x2="46" y2="16" stroke={iconColor} strokeWidth="4" />
            {/* Trend line */}
            <path d="M16 40 L26 32 L36 24 L46 14" stroke={iconColor} strokeWidth="2.5" />
          </g>
        );
      }
    }
  };
  
  return (
    <div
      className={`product-svg-icon-container ${className}`}
      style={{
        backgroundColor: bgColor,
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        boxShadow: isDarkMode 
          ? '0 1px 3px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2)' 
          : '0 1px 2px rgba(0, 0, 0, 0.04)',
        transition: 'all 0.15s ease',
      }}
    >
      {typeof getIconConfig().logoUrl === 'string' ? (
        <img
          src={getIconConfig().logoUrl}
          alt=""
          aria-hidden="true"
          style={{
            objectFit: 'contain',
            display: 'block',
            ...getIconConfig().imageStyle,
          }}
        />
      ) : (
        <svg
          width={size * 0.75}
          height={size * 0.75}
          viewBox={viewBox}
          className="product-svg-icon"
          style={{ display: 'block' }}
        >
          {renderIcon()}
        </svg>
      )}
    </div>
  );
};

